import { Spacing4, Spacing8 } from 'autogen/design-tokens/tokens';
import { Typography } from 'components/generic/typography';
import {
    InvalidInputFocusStyle,
    InvalidInputStyle,
    ValidInputDisabledStyle,
    ValidInputStyle
} from 'components/shared/inputs/theme';
import React, { useId, useState } from 'react';
import styled, { css } from 'styled-components';
import type { BaseInputProps } from '../shared/types';
import Flex from 'components/Layout/components/Flex';
import HelperText from '../components/HelperText';

const Container = styled.div`
    display: inline-flex;
`;

const LabelContainer = styled.label`
    display: inline-flex;
    align-items: start;
    gap: ${Spacing8};
    margin: 0;
`;

const Input = styled.input`
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
`;

const Graphics = styled.div<{ isInvalid?: boolean }>`
    position: relative;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    ${ValidInputStyle}

    ${Input}:hover + & {
        &:after {
            content: '';
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            border-radius: 50%;
        }
    }

    ${Input}:focus + & {
        outline: ${({ theme }) => `solid 2px ${theme.ColorBorderFocus};`};
        outline-offset: ${Spacing4};
    }

    ${Input}:disabled + & {
        ${ValidInputDisabledStyle}
    }

   

    ${Input}:checked + & {
        border-color: ${({ theme, isInvalid }) => (!isInvalid ? theme.ColorBorderBrand : theme.ColorBorderError)};
        background-color: ${({ theme, isInvalid }) => (!isInvalid ? theme.ColorBackgroundBrand : theme.ColorBackgroundError)};

        &:after {
            content: '';
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            background-color: ${({ theme }) => theme.ColorIconPrimaryInverted};
            border-radius: 50%;
        }
    }

    ${({ isInvalid, theme }) =>
        isInvalid &&
        css`
            border: 1px solid ${theme.ColorBorderError};
            ${InvalidInputStyle}

            ${Input}:required:invalid + & {
                ${InvalidInputStyle}
            }

            ${Input}:invalid:focus + & {
                ${InvalidInputFocusStyle}
            }
        `}

    ${Input}:checked:disabled + & {
        ${ValidInputDisabledStyle}
    }
`;

type ControlledRadioProps = React.InputHTMLAttributes<HTMLInputElement> & BaseInputProps;

export const ControlledRadio = React.forwardRef<HTMLInputElement, ControlledRadioProps>((props, ref) => {
    const { isInvalid: controlledInvalidState, label, helperText } = props;
    const [isInvalidUncontrolledState, setIsInvalidUncontrolledState] = useState<boolean>(false);
    const isInvalid = controlledInvalidState || isInvalidUncontrolledState;
    const helperTextId = useId();

    const radio = (
        <Container>
            <Input
                ref={ref}
                type="radio"
                onInvalid={() => setIsInvalidUncontrolledState(true)}
                aria-describedby={helperTextId}
                {...props}
            />
            <Graphics isInvalid={isInvalid} />
        </Container>
    );

    if (label) {
        return (
            <LabelContainer>
                {radio}
                <Flex.Column gap={Spacing4}>
                    <Typography.Label size="M">{label}</Typography.Label>
                    {helperText && <HelperText helperText={helperText} helperTextId={helperTextId} />}
                </Flex.Column>
            </LabelContainer>
        );
    }

    return radio;
});

export default ControlledRadio;
